import React, { useEffect, useState } from 'react';
import { Modal, Row, Col, ListGroup, Spinner } from 'react-bootstrap';
import { db } from 'config/firebase';
import { collection, query, where, getDocs, writeBatch, doc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { usePricingCalculation } from '../hooks/usePricingCalculation';

/**
 * Get a Firestore document reference for a product.
 * Works with both Firestore-sourced data (has .ref) and BigQuery-sourced data (has ._firestorePath).
 *
 * @param {object} product - Product object from grid
 * @returns {DocumentReference|null} Firestore document reference or null if not available
 */
const getProductRef = (product) => {
    // If product already has a Firestore ref, use it
    if (product.ref) {
        return product.ref;
    }

    // If product has a Firestore path (from BigQuery), create a ref from the path
    if (product._firestorePath) {
        // _firestorePath is like: productCatalog/topicz/products/430299/variants/430299
        return doc(db, product._firestorePath);
    }

    // Fallback: try to construct the path from known fields
    // For variants: productCatalog/topicz/products/{parentSku}/variants/{sku}
    // For products: productCatalog/topicz/products/{sku}
    const sku = product.sku || product.docId;
    const parentSku = product.parentSku || product.parent_docId || sku?.split('-')[0];

    if (!sku) {
        console.warn('[getProductRef] Cannot create ref: no SKU for product', product);
        return null;
    }

    // Check if this is a variant (has parentSku different from sku, or sku contains hyphen)
    const isVariant = parentSku && (parentSku !== sku || sku.includes('-'));

    if (isVariant) {
        // Variant path: productCatalog/topicz/products/{parentSku}/variants/{docId}
        const variantDocId = product.docId || sku;
        return doc(db, 'productCatalog', 'topicz', 'products', parentSku, 'variants', variantDocId);
    } else {
        // Core SKU - still stored in variants collection with same SKU as docId
        return doc(db, 'productCatalog', 'topicz', 'products', sku, 'variants', sku);
    }
};

/**
 * Modal for assigning templates to selected products.
 *
 * For pricing templates: Calculates prices using usePricingCalculation hook
 * and applies immediately on selection (no preview step per CONTEXT.md decision).
 *
 * For shipping templates: Assigns template ID only (no calculation).
 */
export const AssignTemplateModal = ({ type, show, setShow, data, gridRef, setRefetch }) => {
    const [savedTemplates, setSavedTemplates] = useState([]);
    const [isApplying, setIsApplying] = useState(false);

    // Pricing calculation hook for type='pricing'
    const { calculateBatchPrices, isCalculating } = usePricingCalculation();

    let templateType = '';
    switch (type) {
        case 'pricing':
            templateType = 'pricingTemplate';
            break;
        case 'shipping':
            templateType = 'shippingTemplate';
            break;
        default:
            break;
    }

    useEffect(() => {
        if (show && type) {
            getSavedTemplates(type).then((savedTemplates) => {
                setSavedTemplates(savedTemplates);
            });
        }
    }, [show, type]);

    const onClose = () => {
        setSavedTemplates([]);
        setIsApplying(false);
        setShow(false);
    };

    /**
     * Handle template selection - applies IMMEDIATELY (no preview).
     * For pricing templates: calculates prices and updates products.
     * For shipping templates: just assigns template ID.
     */
    const handleApplyTemplate = async (template) => {
        if (!template || !data || data.length === 0) return;

        setIsApplying(true);

        try {
            if (type === 'pricing') {
                // Calculate prices for all products using the pricing hook
                const { results, errors, summary } = await calculateBatchPrices(data, template);

                if (errors.length > 0) {
                    console.warn('[AssignTemplateModal] Calculation errors:', errors);
                }

                // Build a map of SKU -> calculated price for quick lookup
                const priceMap = new Map();
                results.forEach(r => {
                    priceMap.set(r.sku, r.breakdown);
                });

                console.log('[AssignTemplateModal] Calculation results:', { results: results.length, errors: errors.length, priceMap: Object.fromEntries(priceMap) });

                // Update all products in Firestore with template and calculated price
                const batch = writeBatch(db);
                let skippedCount = 0;
                for (const product of data) {
                    const docRef = getProductRef(product);
                    if (!docRef) {
                        console.warn('[AssignTemplateModal] No ref for product:', product.sku);
                        skippedCount++;
                        continue;
                    }

                    const breakdown = priceMap.get(product.sku);
                    const updateData = {
                        pricingTemplateId: template.id,
                        pricingTemplateName: template.name
                    };

                    // Add calculated price if we have it
                    if (breakdown) {
                        updateData.calculatedPrice = breakdown.price;
                        updateData.priceBreakdown = breakdown;
                    }

                    batch.update(docRef, updateData);
                }
                await batch.commit();

                // Update grid rows client-side immediately (BigQuery sync is delayed)
                if (gridRef?.current?.api) {
                    const updatedRows = [];
                    data.forEach(product => {
                        const breakdown = priceMap.get(product.sku);
                        if (breakdown) {
                            updatedRows.push({
                                ...product,
                                calculatedPrice: breakdown.price,
                                priceBreakdown: breakdown,
                                pricingTemplateId: template.id,
                                pricingTemplateName: template.name,
                            });
                        } else {
                            updatedRows.push({
                                ...product,
                                pricingTemplateId: template.id,
                                pricingTemplateName: template.name,
                            });
                        }
                    });
                    console.log('[AssignTemplateModal] Updating grid rows:', updatedRows.map(r => ({ sku: r.sku, docId: r.docId, calculatedPrice: r.calculatedPrice })));
                    if (updatedRows.length > 0) {
                        const result = gridRef.current.api.applyTransaction({ update: updatedRows });
                        console.log('[AssignTemplateModal] applyTransaction result:', { updated: result?.update?.length || 0 });
                        if (result?.update) {
                            gridRef.current.api.refreshCells({ rowNodes: result.update });
                        }
                    }
                }

                toast.success(`Pricing template applied to ${summary.calculated} products`, { theme: 'colored' });
                if (summary.skipped > 0) {
                    toast.warning(`${summary.skipped} products skipped (missing cost)`, { theme: 'colored' });
                }
            } else {
                // Shipping template: just assign template ID
                const batch = writeBatch(db);
                let appliedCount = 0;
                for (const product of data) {
                    const docRef = getProductRef(product);
                    if (!docRef) {
                        console.warn('[AssignTemplateModal] No ref for product:', product.sku);
                        continue;
                    }
                    batch.update(docRef, {
                        [templateType]: template.id
                    });
                    appliedCount++;
                }
                await batch.commit();

                // Update grid rows client-side immediately
                if (gridRef?.current?.api) {
                    const updatedRows = data.map(product => ({
                        ...product,
                        [templateType]: template.id,
                    }));
                    const result = gridRef.current.api.applyTransaction({ update: updatedRows });
                    if (result?.update) {
                        gridRef.current.api.refreshCells({ rowNodes: result.update });
                    }
                }

                toast.success(`Template assigned to ${appliedCount} products`, { theme: 'colored' });
            }

            // Close modal (grid was already updated client-side via applyTransaction)
            onClose();
        } catch (error) {
            console.error('[AssignTemplateModal] Error applying template:', error);
            toast.error(error.message, { theme: 'colored' });
            setIsApplying(false);
        }
    };

    const isLoading = isApplying || isCalculating;

    return (
        <Modal
            show={show}
            onHide={onClose}
            backdrop="static"
            keyboard={false}
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    Assign {type === 'pricing' ? 'Pricing' : 'Shipping'} Template ({data.length} products)
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isLoading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" size="sm" className="me-2" />
                        <span>Applying template...</span>
                    </div>
                ) : savedTemplates.length === 0 ? (
                    <div className="text-center text-muted py-4">
                        No {type} templates found. Create one in Settings first.
                    </div>
                ) : (
                    <Row>
                        <Col>
                            <p className="text-muted small mb-2">
                                Click a template to apply it immediately.
                            </p>
                            <ListGroup>
                                {savedTemplates.map((template) => (
                                    <ListGroup.Item
                                        key={template.id}
                                        action
                                        onClick={() => handleApplyTemplate(template)}
                                        className="d-flex justify-content-between align-items-center"
                                    >
                                        <span>{template.name}</span>
                                        {type === 'pricing' && template.settings?.prefMargin && (
                                            <small className="text-muted">
                                                {Math.round(template.settings.prefMargin * 100)}% margin
                                            </small>
                                        )}
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        </Col>
                    </Row>
                )}
            </Modal.Body>
        </Modal>
    );
};

const getSavedTemplates = async (type) => {
    const q = query(collection(db, 'systemTemplates'), where('type', '==', type));
    const querySnapshot = await getDocs(q);
    const templates = [];
    querySnapshot.forEach((doc) => {
        templates.push({ id: doc.id, ref: doc.ref, ...doc.data() });
    });
    return templates;
};
